import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Typography,
  Collapse,
  Chip,
} from "@mui/material";
import {
  Delete,
  Edit,
  ExpandMore as ExpandMoreIcon,
  Check,
  CheckCircle,
  Close,
  SaveAlt,
} from "@mui/icons-material";
import { useState } from "react";
import MathContent from "../../../../../customComponents/MathContent";

const QuestionCard = ({ question, index, onEdit, onDelete, onSave, isSaved, isSaving }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState(question);

  const handleSaveEdit = () => {
    onEdit(editedQuestion);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedQuestion(question);
    setIsEditing(false);
  };

  const isMultipleChoice = (editedQuestion.options && editedQuestion.options.length > 0);

  return (
    <Card
      sx={{
        mb: 2,
        border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
        borderRadius: "10px",
        opacity: isSaved ? 0.7 : 1,
        transition: "opacity 0.3s",
      }}
    >
      <CardContent sx={{ pb: "12px !important" }}>
        {/* Header: number + chips + edit/delete */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "primary.contrastText",
                backgroundColor: "primary.main",
                borderRadius: "6px",
                px: 1,
                py: 0.25,
                lineHeight: 1.8,
              }}
            >
              Q{index + 1}
            </Typography>
            {editedQuestion.topic && (
              <Chip label={editedQuestion.topic} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
            )}
            {editedQuestion.level && (
              <Chip label={editedQuestion.level} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
            )}
          </Box>
          {!isSaved && (
            <Box sx={{ display: "flex", flexShrink: 0 }}>
              {isEditing ? (
                <>
                  <IconButton size="small" onClick={handleSaveEdit} color="success">
                    <Check sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelEdit} color="error">
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small" onClick={() => setIsEditing(true)}>
                    <Edit sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" onClick={onDelete} color="error">
                    <Delete sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              )}
            </Box>
          )}
          {isSaved && (
            <CheckCircle sx={{ color: "#4CAF50", fontSize: 20, flexShrink: 0, mt: 0.25 }} />
          )}
        </Box>

        {/* Question Body */}
        <Box sx={{ mb: 1.5 }}>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editedQuestion.body}
              onChange={(e) =>
                setEditedQuestion({ ...editedQuestion, body: e.target.value })
              }
              variant="outlined"
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
              <MathContent text={editedQuestion.body} />
            </Typography>
          )}
        </Box>

        {/* Options */}
        {isMultipleChoice && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {editedQuestion.options.map((option, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    backgroundColor: option.is_answer ? "#E8F5E9" : (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                    borderRadius: "6px",
                    borderLeft: option.is_answer
                      ? "3px solid #4CAF50"
                      : "3px solid transparent",
                  }}
                >
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        value={option.body}
                        onChange={(e) => {
                          const newOptions = editedQuestion.options.map((o, i) =>
                            i === idx ? { ...o, body: e.target.value } : o
                          );
                          setEditedQuestion({ ...editedQuestion, options: newOptions });
                        }}
                        variant="outlined"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newOptions = editedQuestion.options.map((o, i) =>
                            i === idx ? { ...o, is_answer: !o.is_answer } : o
                          );
                          setEditedQuestion({ ...editedQuestion, options: newOptions });
                        }}
                        color={option.is_answer ? "success" : "default"}
                      >
                        <Check sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontSize: "0.8rem",
                        color: option.is_answer ? "#2E7D32" : "text.secondary",
                        fontWeight: option.is_answer ? 600 : 400,
                      }}
                    >
                      <MathContent text={option.body} />
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Collapsible Explanation */}
        {editedQuestion.explanation && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: "text.secondary",
                mb: 0.5,
              }}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Explanation
              </Typography>
            </Box>
            <Collapse in={isExpanded}>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={editedQuestion.explanation}
                  onChange={(e) =>
                    setEditedQuestion({
                      ...editedQuestion,
                      explanation: e.target.value,
                    })
                  }
                  variant="outlined"
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.8rem", pl: 0.5 }}
                >
                  <MathContent text={editedQuestion.explanation} />
                </Typography>
              )}
            </Collapse>
          </Box>
        )}

        {/* Save button */}
        {!isSaved && !isEditing && (
          <>
            <Divider sx={{ mt: 1.5, mb: 1.25 }} />
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => onSave(editedQuestion)}
              disabled={isSaving}
              startIcon={
                isSaving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SaveAlt sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              {isSaving ? "Saving..." : "Save to Question Bank"}
            </Button>
          </>
        )}

        {isSaved && (
          <Typography
            variant="caption"
            sx={{ color: "#4CAF50", fontWeight: 600, mt: 1, display: "block", textAlign: "center" }}
          >
            Saved to question bank
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionCard;
